import {
  ShoppingCart,
  HelpCircle,
  Package,
  Clock,
  MapPin,
  Truck,
  CreditCard,
  Timer,
  MessageCircle,
  RotateCcw,
  Menu,
  ChevronRight,
} from 'lucide-react';
import { readLastOrderForBusiness } from '@/lib/localLastOrder';
import { CLIENT_ORDER_REQUEST_MESSAGE } from '@/lib/orders/clientOrderRequestTemplate';
import {
  formatOrderSummaryText,
  getOrderToRepeat,
  loadOrderIntoCart,
  orderItemsToCartLines,
} from '@/lib/assistant/assistantRepeatOrder';
import { getLocalSuggestedUpsellProducts } from '@/lib/assistant/assistantOrderCloseFlow';
import {
  CHECKOUT_PAYMENT_LABELS,
  getEnabledPaymentMethodsForCheckout,
} from '@/lib/checkout/paymentMethods';
import { formatScheduleForCustomer } from '@/lib/weeklySchedule';
import { openWhatsAppUrl } from '@/lib/checkoutWhatsAppOrder';
import { logStructured } from '@/lib/networkLog';

/** Menú principal, pedido, preguntas frecuentes y pedido anterior. */
export function bindAssistantChatMenus(ctx) {
  const {
    businessId,
    menuData,
    lastOrder,
    repeatOrderAvailable,
    setCurrentStep,
    sessionMemoryRef,
    productFlowRef,
    getCartCount,
    addBotMessage,
    addUserMessage,
    openAssistantCheckout,
    showCheckoutPreferencePrompt,
    showCartSummaryInChat,
    touchCheckoutFromAssistant,
    confirmRepeatSameData,
    confirmRepeatWithChanges,
    addToCart,
    handleMainChoiceRef,
    proceedToCheckoutRef,
    showMainMenuRef,
  } = ctx;

  const showMainMenu = () => {
    setCurrentStep('main');
    sessionMemoryRef.current.guidedOrderActive = false;
    sessionMemoryRef.current.guidedOrderDraft = null;
    sessionMemoryRef.current.assistantEntryMode = null;
    const canRepeat =
      repeatOrderAvailable ||
      !!readLastOrderForBusiness(businessId)?.items?.length ||
      !!(lastOrder?.items?.length > 0);
    const opts = [
      {
        label: 'Armar pedido',
        value: 'order',
        icon: ShoppingCart,
        action: () => handleMainChoice('order'),
      },
      {
        label: 'Una consulta',
        value: 'question',
        icon: HelpCircle,
        action: () => handleMainChoice('question'),
      },
    ];
    if (canRepeat) {
      opts.push({
        label: 'Repetir último pedido',
        value: 'previous',
        icon: Package,
        action: () => handleMainChoice('previous'),
      });
    }
    addBotMessage('¿Prefieres una recomendación o armar el pedido?', opts);
  };

  const showKnowWhatToOrderPrompt = () => {
    setCurrentStep('know-order');
    sessionMemoryRef.current.assistantEntryMode = 'know-order';
    sessionMemoryRef.current.guidedOrderActive = false;
    sessionMemoryRef.current.guidedOrderDraft = null;
    addBotMessage(
      `${CLIENT_ORDER_REQUEST_MESSAGE}\n\n💡 Podés pegar todo junto (Enter para bajar de línea y tocá enviar) o ir respondiendo paso a paso en el chat.`,
      [
      {
        label: 'Ver menú',
        value: 'show-menu',
        icon: Menu,
        action: () => {
          addUserMessage('Ver menú');
          showOrderOptions();
        },
      },
      {
        label: 'Al inicio',
        value: 'back',
        icon: ChevronRight,
        action: () => showMainMenu(),
      },
    ]);
  };

  const showOrderOptions = () => {
    setCurrentStep('order');
    const pf = productFlowRef.current;
    if (pf?.showOrderMenuBrowse) {
      pf.showOrderMenuBrowse();
      return;
    }
    setTimeout(() => {
      addBotMessage('Por ahora no hay menú cargado. Intentá de nuevo en un momento.');
      showBackToMenu();
    }, 300);
  };

  const handleOrderChoiceFromText = (choice) => {
    setCurrentStep('order');
    const pf = productFlowRef.current;
    if (!pf) return;
    switch (choice) {
      case 'solo':
        pf.showProductsForSolo();
        break;
      case 'duo':
        pf.showProductsForDuo();
        break;
      case 'group':
        pf.showProductsForGroup();
        break;
      case 'surprise':
        pf.showSurpriseMe();
        break;
      case 'cheap':
        pf.showCheapestProducts();
        break;
      case 'special':
        pf.showSpecialProducts();
        break;
      case 'categories':
        pf.showSmartSuggestions();
        break;
      case 'popular':
        pf.showMostOrderedProducts();
        break;
      default:
        break;
    }
  };

  const handleOrderChoice = (choice) => {
    const labels = {
      solo: 'Solo para mí',
      duo: 'Para 2 personas',
      group: 'Para varias personas',
      surprise: 'Sorpréndeme',
      cheap: 'Algo económico',
      special: 'Algo recomendado',
      categories: 'Por categorías',
    };
    addUserMessage(labels[choice]);
    handleOrderChoiceFromText(choice);
  };

  const handleMainChoiceFromText = (choice) => {
    switch (choice) {
      case 'order':
      case 'browse-menu':
        showOrderOptions();
        break;
      case 'know-order':
        showKnowWhatToOrderPrompt();
        break;
      case 'question':
        showQuestionOptions();
        break;
      case 'previous':
        showRepeatConfirmFlow();
        break;
      default:
        break;
    }
  };

  const showBackToMenu = () => {
    setTimeout(() => {
      addBotMessage('¿Necesitas algo más?', [
        {
          label: 'Ver recomendaciones',
          value: 'menu',
          icon: Menu,
          action: () => handleMainChoice('order'),
        },
        {
          label: 'Al inicio',
          value: 'back',
          icon: ChevronRight,
          action: () => showMainMenu(),
        },
      ]);
    }, 300);
  };

  const showProductActions = () => {
    setTimeout(() => {
      addBotMessage('¿Quieres agregar algo más?', [
        {
          label: 'Ver recomendaciones',
          value: 'menu',
          icon: Menu,
          action: () => handleMainChoice('order'),
        },
        ...(getCartCount() > 0
          ? [
              {
                label: 'Ver lo que llevás',
                value: 'cart-view',
                icon: ShoppingCart,
                action: () => showCartSummaryInChat(),
              },
              {
                label: 'Completa tus datos para terminar el pedido',
                value: 'cart-chat',
                icon: MessageCircle,
                action: () => showCheckoutPreferencePrompt(),
              },
            ]
          : []),
        {
          label: 'Al inicio',
          value: 'back',
          icon: ChevronRight,
          action: () => showMainMenu(),
        },
      ]);
    }, 300);
  };

  const handleMainChoice = (choice, repeatOrderPin = null) => {
    switch (choice) {
      case 'order':
      case 'browse-menu':
        addUserMessage(choice === 'browse-menu' ? 'No sé qué pedir' : 'Armar pedido');
        sessionMemoryRef.current.assistantEntryMode = 'browse';
        showOrderOptions();
        break;
      case 'know-order':
        addUserMessage('Sé qué pedir');
        showKnowWhatToOrderPrompt();
        break;
      case 'question':
        addUserMessage('Una consulta');
        showQuestionOptions();
        break;
      case 'previous':
        addUserMessage('Repetir mi último pedido');
        showRepeatConfirmFlow(repeatOrderPin);
        break;
      default:
        break;
    }
  };

  handleMainChoiceRef.current = handleMainChoice;
  proceedToCheckoutRef.current = () => {
    if (getCartCount() > 0) {
      showCheckoutPreferencePrompt();
    } else {
      handleMainChoice('order');
    }
  };
  showMainMenuRef.current = showMainMenu;

  const showQuestionOptions = () => {
    setCurrentStep('question');
    setTimeout(() => {
      addBotMessage(
        '¿Qué te gustaría saber?',
        [
          {
            label: 'Horario',
            value: 'schedule',
            icon: Clock,
            action: () => handleQuestionChoice('schedule'),
          },
          {
            label: 'Dónde quedan',
            value: 'location',
            icon: MapPin,
            action: () => handleQuestionChoice('location'),
          },
          {
            label: 'Delivery y precio',
            value: 'delivery',
            icon: Truck,
            action: () => handleQuestionChoice('delivery'),
          },
          {
            label: 'Formas de pago',
            value: 'payment',
            icon: CreditCard,
            action: () => handleQuestionChoice('payment'),
          },
          {
            label: 'Cuánto demora',
            value: 'wait',
            icon: Timer,
            action: () => handleQuestionChoice('wait'),
          },
          {
            label: 'Otra cosa',
            value: 'other',
            icon: MessageCircle,
            action: () => handleQuestionChoice('other'),
          },
        ]
      );
    }, 300);
  };

  const handleQuestionChoice = (choice) => {
    const labels = {
      schedule: 'Horario',
      location: 'Dónde quedan',
      delivery: 'Delivery y precio',
      payment: 'Formas de pago',
      wait: 'Cuánto demora',
      other: 'Otra cosa',
    };
    addUserMessage(labels[choice]);

    const business = menuData?.business || {};

    setTimeout(() => {
      switch (choice) {
        case 'schedule': {
          const scheduleText = formatScheduleForCustomer(business);
          if (scheduleText) {
            addBotMessage(
              '\u{1F550} Horario\n\n' +
                scheduleText +
                '\n\n' +
                (business.is_open
                  ? '\u2705 Abierto ahora'
                  : '\u{1F534} ' + (business.closed_message || 'Cerrado'))
            );
          } else {
            addBotMessage(
              '\u{1F550} Todavía no tenemos el horario cargado. Escríbenos por WhatsApp y te lo confirmamos.'
            );
          }
          showBackToMenu();
          break;
        }

        case 'location':
          if (business.address) {
            addBotMessage(
              `📍 Estamos en\n\n${business.address}\n\n` +
                (business.reference ? `📝 Referencia: ${business.reference}` : '')
            );
          } else if (business.business_type === 'dark_kitchen') {
            addBotMessage('📍 Somos dark kitchen: solo delivery. Te lo llevamos donde estés.');
          } else {
            addBotMessage('📍 No tenemos la dirección cargada. Escríbenos por WhatsApp.');
          }
          showBackToMenu();
          break;

        case 'delivery':
          if (business.service_types?.includes('delivery')) {
            let msg = '🚚 Delivery\n\n';
            msg += `💵 Precio base: $${(business.delivery_base_price || 0).toLocaleString('es-CL')}\n`;
            if (business.delivery_price_per_km) {
              msg += `📏 Por km adicional: $${business.delivery_price_per_km.toLocaleString('es-CL')}\n`;
            }
            if (business.delivery_radius_km) {
              msg += `📍 Radio de cobertura: ${business.delivery_radius_km} km\n`;
            }
            addBotMessage(msg);
          } else {
            addBotMessage('🚚 Por ahora no hacemos delivery. ¡Te esperamos en el local!');
          }
          showBackToMenu();
          break;

        case 'payment': {
          const { methodIds } = getEnabledPaymentMethodsForCheckout(business);
          let paymentMsg = '💳 Puedes pagar así\n\n';
          for (const id of methodIds) {
            if (id === 'efectivo') {
              paymentMsg += '• 💵 Efectivo\n';
            } else if (id === 'transferencia') {
              paymentMsg += business.bank_name
                ? `• 🏦 Transferencia a ${business.bank_name}\n`
                : `• 🏦 ${CHECKOUT_PAYMENT_LABELS.transferencia}\n`;
              if (business.bank_account_number) {
                paymentMsg += `   Cuenta: ${business.bank_account_number}\n`;
              }
              if (business.bank_rut) {
                paymentMsg += `   RUT: ${business.bank_rut}\n`;
              }
              if (business.bank_holder_name) {
                paymentMsg += `   Titular: ${business.bank_holder_name}\n`;
              }
            } else if (id === 'online_manual_card') {
              paymentMsg += '• 💳 Tarjeta en el local\n';
            } else if (id === 'online_manual_mercadopago') {
              paymentMsg += `• 📱 ${CHECKOUT_PAYMENT_LABELS.online_manual_mercadopago}\n`;
            }
          }
          addBotMessage(paymentMsg);
          showBackToMenu();
          break;
        }

        case 'wait': {
          let waitMsg = '⏱️ Tiempos aproximados\n\n';
          if (business.preparation_time_minutes || business.delivery_time_minutes) {
            if (business.preparation_time_minutes) {
              waitMsg += `• 🍽️ Cocina: ~${business.preparation_time_minutes} min\n`;
            }
            if (business.delivery_time_minutes) {
              waitMsg += `• 🚚 Delivery: ~${business.delivery_time_minutes} min\n`;
            }
            if (business.preparation_time_minutes && business.service_types?.includes('pickup')) {
              waitMsg += `• 📦 Retiro: ~${business.preparation_time_minutes} min\n`;
            }
          } else {
            waitMsg += '• 🍽️ En local: ~15-25 min\n';
            waitMsg += '• 🚚 Delivery: ~30-45 min\n';
            waitMsg += '• 📦 Retiro: ~15-20 min\n';
          }
          waitMsg += '\n(Puede variar si está lleno.)';
          addBotMessage(waitMsg);
          showBackToMenu();
          break;
        }

        case 'other':
          if (business.whatsapp) {
            const whatsappNumber = business.whatsapp.replace(/\D/g, '');
            const message = encodeURIComponent(`Hola, quería consultar por ${business.name || 'el local'}`);
            openWhatsAppUrl(`https://wa.me/${whatsappNumber}?text=${message}`, {
              businessId,
              source: 'assistant_help',
            });
            addBotMessage('💬 Te dejo el WhatsApp listo.');
          } else {
            logStructured('analytics', {
              event: 'whatsapp_failed',
              businessId,
              source: 'assistant_help',
              reason: 'no_business_whatsapp',
            });
            addBotMessage('💬 No tenemos WhatsApp cargado. Prueba con otra opción del menú.');
          }
          showBackToMenu();
          break;

        default:
          showBackToMenu();
      }
    }, 500);
  };

  const showRepeatDataChoice = (orderToShow) => {
    addBotMessage('¿Querés mantener tus datos anteriores (dirección y pago) o cambiarlos?', [
      {
        label: 'Mantener la información anterior',
        value: 'repeat-same',
        icon: RotateCcw,
        variant: 'emphasis',
        action: () => confirmRepeatSameData(orderToShow),
      },
      {
        label: 'Cambiar dirección o forma de pago',
        value: 'repeat-change',
        icon: CreditCard,
        action: () => confirmRepeatWithChanges(orderToShow),
      },
    ]);
  };

  const showRepeatAddMorePrompt = (orderToShow) => {
    addBotMessage('¿Querés agregar algo más a este pedido?', [
      {
        label: 'Sí, agregar algo más',
        value: 'repeat-add-more',
        icon: ShoppingCart,
        variant: 'outline',
        action: () => {
          addUserMessage('Agregar algo más');
          loadOrderIntoCart(orderToShow, addToCart, businessId);
          showOrderOptions();
        },
      },
      {
        label: 'No, continuar con este pedido',
        value: 'repeat-continue',
        icon: ChevronRight,
        variant: 'emphasis',
        action: () => {
          addUserMessage('Continuar con este pedido');
          showRepeatDataChoice(orderToShow);
        },
      },
    ]);
  };

  const showRepeatConfirmFlow = (pinnedOrder = null) => {
    setCurrentStep('repeat');
    const orderToShow =
      pinnedOrder?.items?.length > 0 ? pinnedOrder : getOrderToRepeat(businessId, lastOrder);

    if (!orderToShow?.items?.length) {
      addBotMessage('No tengo un pedido anterior guardado. ¿Querés ver la carta o pedir con ayuda?');
      setTimeout(() => {
        addBotMessage(null, [
          {
            label: 'Ver recomendaciones',
            value: 'menu',
            icon: Menu,
            action: () => handleMainChoice('order'),
          },
          {
            label: 'Al inicio',
            value: 'back',
            icon: ChevronRight,
            action: () => showMainMenu(),
          },
        ]);
      }, 300);
      return;
    }

    addBotMessage(formatOrderSummaryText(orderToShow));

    setTimeout(() => {
      const upsellProducts = getLocalSuggestedUpsellProducts(
        menuData,
        orderItemsToCartLines(orderToShow),
        3
      );
      if (upsellProducts.length > 0) {
        addBotMessage('¿Te tienta sumar algo más al pedido?', null, null, {
          suggestedProducts: upsellProducts,
        });
        setTimeout(() => showRepeatAddMorePrompt(orderToShow), 400);
      } else {
        showRepeatAddMorePrompt(orderToShow);
      }
    }, 300);
  };

  return {
    showMainMenu,
    handleMainChoice,
    handleMainChoiceFromText,
    showOrderOptions,
    showKnowWhatToOrderPrompt,
    handleOrderChoice,
    handleOrderChoiceFromText,
    showBackToMenu,
    showProductActions,
    showQuestionOptions,
    handleQuestionChoice,
    showRepeatConfirmFlow,
  };
}
